import React, { useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { StickyNote, User, Clock } from "lucide-react";
import { formatDate } from "../lib/api";

// Renders a button showing the Alvys note count; opens a dialog listing the notes.
// `variant` "button" (default, full button) or "icon" (compact icon button for tables).
export const AlvysNotesButton = ({ notes, unitNumber, variant = "button", testid }) => {
  const [open, setOpen] = useState(false);
  const list = Array.isArray(notes) ? notes : [];
  const count = list.length;

  if (variant === "icon") {
    return (
      <>
        <Button
          size="icon"
          variant="ghost"
          disabled={count === 0}
          data-testid={testid || "alvys-notes-icon"}
          aria-label={`Alvys notes (${count})`}
          title={count ? `${count} Alvys note(s)` : "No Alvys notes"}
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className="relative"
        >
          <StickyNote className="h-4 w-4" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {count}
            </span>
          )}
        </Button>
        <NotesDialog open={open} setOpen={setOpen} list={list} unitNumber={unitNumber} />
      </>
    );
  }

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        className="gap-1.5 h-7"
        disabled={count === 0}
        data-testid={testid || "alvys-notes-button"}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <StickyNote className="h-3.5 w-3.5" /> Alvys notes{count ? ` (${count})` : ""}
      </Button>
      <NotesDialog open={open} setOpen={setOpen} list={list} unitNumber={unitNumber} />
    </>
  );
};

const NotesDialog = ({ open, setOpen, list, unitNumber }) => (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogContent data-testid="alvys-notes-dialog" className="max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <StickyNote className="h-4 w-4" /> Alvys notes{unitNumber ? ` — Unit ${unitNumber}` : ""}
        </DialogTitle>
        <DialogDescription>
          {list.length} note{list.length === 1 ? "" : "s"} from Alvys for this unit (most recent first).
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2.5 py-1">
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">No notes recorded for this unit.</p>
        ) : (
          list.map((n, i) => (
            <div key={n.id || i} data-testid="alvys-note-item" className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm whitespace-pre-wrap break-words">{n.description}</p>
                {n.note_type && (
                  <span className="shrink-0 rounded-full border border-border bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{n.note_type}</span>
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                {n.user && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {n.user}</span>}
                {n.time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDate(n.time)}</span>}
              </div>
            </div>
          ))
        )}
      </div>
    </DialogContent>
  </Dialog>
);
